/**
 * FILE: assets/demand-tracking.js
 *
 * PURPOSE:
 *   Records a GA4 + Meta event every time a shopper completes a vehicle
 *   selection in the cascade picker (Model -> Generation -> Option), tagged with
 *   availability (available / coming_soon / out_of_stock). This quantifies how
 *   much high-intent ad traffic lands on products we can't sell yet, broken down
 *   by campaign, so CPA can be attributed to unfillable demand.
 *
 * HOW IT'S CALLED:
 *   assets/cascade-picker.js calls window.YLDemand.track(variant, availability, ctx)
 *   from resolveVariant() once all three tiers resolve to a real variant.
 *
 * ATTRIBUTION:
 *   First-touch utm/gclid/fbclid are captured site-wide in layout/theme.liquid and
 *   stored in sessionStorage under 'yl_fts'. GA4 and Meta also stamp their own
 *   session attribution automatically; these params are belt-and-suspenders.
 *
 * SAFETY:
 *   Every path is wrapped so a missing/blocked tag or storage never breaks the
 *   picker. Events are de-duplicated per variant per session.
 */

(function () {
  'use strict';

  var FTS_KEY = 'yl_fts';        // first-touch source (set in theme.liquid)
  var FIRED_KEY = 'yl_vehsel';   // variant ids already reported this session

  function readJSON(key) {
    try { return JSON.parse(sessionStorage.getItem(key)) || {}; }
    catch (e) { return {}; }
  }

  function alreadyFired(id) {
    return !!readJSON(FIRED_KEY)[id];
  }

  function markFired(id) {
    try {
      var s = readJSON(FIRED_KEY);
      s[id] = 1;
      sessionStorage.setItem(FIRED_KEY, JSON.stringify(s));
    } catch (e) { /* no-op */ }
  }

  window.YLDemand = {
    /**
     * @param {Object} variant       Shopify variant (from product.variants | json)
     * @param {String} availability  'available' | 'coming_soon' | 'out_of_stock'
     * @param {Object} ctx           { model, generation, option, product }
     */
    track: function (variant, availability, ctx) {
      try {
        if (!variant || !variant.id) return;
        var id = String(variant.id);
        if (alreadyFired(id)) return; // one event per variant per session

        ctx = ctx || {};
        var ft = readJSON(FTS_KEY);

        var payload = {
          model:       ctx.model || '',
          generation:  ctx.generation || '',
          option:      ctx.option || '',
          variant_id:  variant.id,
          sku:         variant.sku || '',
          product:     ctx.product || '',
          // Shopify variant.price is in cents — convert to a readable amount.
          price:       (typeof variant.price === 'number') ? (variant.price / 100) : variant.price,
          availability: availability || '',
          source:   ft.source   || '',
          medium:   ft.medium   || '',
          campaign: ft.campaign || '',
          gclid:    ft.gclid    || '',
          fbclid:   ft.fbclid   || ''
        };

        // --- GA4 ---
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'vehicle_selected', payload);
        }

        // --- Meta Pixel ---
        // Custom event (not a standard event) so it never interferes with
        // Purchase/AddToCart optimization, but is still available for attribution
        // and for building a retargeting audience of unfilled demand.
        if (typeof window.fbq === 'function') {
          window.fbq('trackCustom', 'VehicleSelected', payload);
        }

        markFired(id);
      } catch (e) {
        /* never break the picker */
      }
    }
  };
})();
